<p-dialog
    [(visible)]="visible"
    [modal]="true"
    [closable]="true"
    [draggable]="false"
    [resizable]="false"
    [style]="{ width: '34rem', height: '900px' }"
    (onHide)="close()"
>
    <ng-template #header>
        <span i18n="@@extraction-configure">Extraktion konfigurieren</span>
    </ng-template>

    <div class="d-flex flex-column gap-3 py-2">
        <p
            class="text-muted mb-0"
            style="font-size: 0.9rem;"
            i18n="@@extraction-intro"
        >
            Die Extraktion liest das Dokument und bereitet es für die KI-Suche vor.
            Ohne Extraktion kann der Bot das Dokument nicht durchsuchen.
            Die Standardeinstellungen funktionieren für die meisten Dokumente.
        </p>
        <div class="d-flex flex-column gap-1">
            <div class="d-flex align-items-center gap-1">
                <label i18n="@@extraction-chunking-strategy">Ergebnisstruktur</label>
            </div>
            <p-select
                [(ngModel)]="extractionConfig.chunking_strategy"
                [options]="chunkingStrategyOptions"
                optionLabel="label"
                optionValue="value"
                appendTo="body"
                [invalid]="isExtractionChunkingStrategyInvalid()"
            >
                <ng-template
                    let-option
                    #item
                >
                    <div class="chunking-option-item">
                        <div>{{ option.label }}</div>
                        @if (option.subtitle) {
                        <small class="chunking-option-subtitle">{{ option.subtitle }}</small>
                        }
                    </div>
                </ng-template>
            </p-select>
            <p-tree
                [value]="extractionParameterTree"
                class="extraction-parameter-tree"
            >
                <ng-template
                    let-node
                    #default
                >
                    @if (node.key === 'max-chars') {
                    <div class="d-flex flex-column gap-1 py-1">
                        <div class="d-flex align-items-center gap-1">
                            <label i18n="@@extraction-max-chars">Abschnittgröße (Zeichen)</label>
                            <i
                                class="ri-information-line text-muted"
                                pTooltip="Wie groß ein Textabschnitt maximal sein darf. Kleiner = präzisere Suchtreffer, größer = mehr Kontext pro Treffer."
                                i18n-pTooltip="@@extraction-max-chars-hint"
                                appendTo="body"
                                tooltipStyleClass="project-documents-dialog-tooltip"
                                tooltipPosition="right"
                            ></i>
                        </div>
                        <p-inputnumber
                            [(ngModel)]="extractionConfig.maxChars"
                            [min]="100"
                            [max]="10000"
                            [invalid]="isExtractionMaxCharsInvalid()"
                        />
                    </div>
                    }
                    @if (node.key === 'max-overlap') {
                    <div class="d-flex flex-column gap-1 py-1">
                        <div class="d-flex align-items-center gap-1">
                            <label i18n="@@extraction-max-overlap">Überschneidung (Zeichen)</label>
                            <i
                                class="ri-information-line text-muted"
                                pTooltip="Wie viele Zeichen benachbarter Abschnitte sich überschneiden. Verhindert, dass Informationen am Abschnittsende verloren gehen."
                                i18n-pTooltip="@@extraction-max-overlap-hint"
                                appendTo="body"
                                tooltipStyleClass="project-documents-dialog-tooltip"
                                tooltipPosition="right"
                            ></i>
                        </div>
                        <p-inputnumber
                            [(ngModel)]="extractionConfig.maxOverlap"
                            [min]="0"
                            [max]="5000"
                            [invalid]="isExtractionMaxOverlapInvalid()"
                        />
                    </div>
                    } @else {
                    <div
                        class="tree-root-click-target"
                        (click)="toggleExtractionParameterTree(node)"
                    >{{ node.label }}</div>
                    }
                </ng-template>
            </p-tree>
        </div>

        <div class="d-flex align-items-center gap-2">
            <p-toggleswitch [(ngModel)]="extractionConfig.imagesEnabled" />
            <label i18n="@@extraction-images-enabled">Bilder analysieren</label>
            <i
                class="ri-information-line text-muted"
                pTooltip="KI beschreibt Bilder im Dokument, sodass auch Bildinhalt durchsucht werden kann. Erhöht die Verarbeitungszeit."
                i18n-pTooltip="@@extraction-images-hint"
                appendTo="body"
                tooltipStyleClass="project-documents-dialog-tooltip"
                tooltipPosition="right"
            ></i>
        </div>
        <div class="d-flex align-items-center gap-2">
            <p-toggleswitch [(ngModel)]="extractionConfig.ocrEnabled" />
            <label i18n="@@extraction-ocr-enabled">OCR aktivieren</label>
            <i
                class="ri-information-line text-muted"
                pTooltip="Texterkennung für gescannte Dokumente und bildbasierte PDFs. Nur aktivieren, wenn das Dokument keinen auswählbaren Text enthält."
                i18n-pTooltip="@@extraction-ocr-hint"
                appendTo="body"
                tooltipStyleClass="project-documents-dialog-tooltip"
                tooltipPosition="right"
            ></i>
        </div>
        <div class="d-flex align-items-center gap-2">
            <p-toggleswitch [(ngModel)]="extractionConfig.detectChapters" />
            <label i18n="@@extraction-detect-chapters">Kapitel erkennen</label>
            <i
                class="ri-information-line text-muted"
                pTooltip="Erkennt die Kapitelstruktur des Dokuments, damit der Bot Treffer einem Kapitel zuordnen und nach Kapiteln gefragt werden kann. Kann bei Dokumenten ohne erkennbare Überschriften einen einmaligen KI-Aufruf auslösen."
                i18n-pTooltip="@@extraction-detect-chapters-hint"
                appendTo="body"
                tooltipStyleClass="project-documents-dialog-tooltip"
                tooltipPosition="right"
            ></i>
        </div>
        <div class="d-flex flex-column gap-1">
            <div class="d-flex align-items-center gap-1">
                <label
                    i18n="@@extraction-ocr-language"
                    [class.text-muted]="!extractionConfig.ocrEnabled"
                >OCR-Sprache</label>
                <i
                    class="ri-information-line text-muted"
                    pTooltip="Sprache(n) des Dokuments für die Texterkennung. Bei mehrsprachigen Dokumenten mehrere Sprachen auswählen."
                    i18n-pTooltip="@@extraction-ocr-language-hint"
                    appendTo="body"
                    tooltipStyleClass="project-documents-dialog-tooltip"
                    tooltipPosition="right"
                ></i>
                @if(!extractionConfig.ocrEnabled) {
                <i
                    class="ri-error-warning-line"
                    style="color: #f59e0b;"
                    pTooltip="OCR muss aktiviert sein"
                    i18n-pTooltip="@@extraction-ocr-required-hint"
                    appendTo="body"
                    tooltipStyleClass="project-documents-dialog-tooltip"
                    tooltipPosition="right"
                ></i>
                }
            </div>
            <p-select
                [(ngModel)]="ocrLanguagesSelected"
                [options]="ocrLanguageOptions"
                [multiple]="true"
                optionLabel="label"
                optionValue="value"
                [disabled]="!extractionConfig.ocrEnabled"
                [showClear]="false"
                appendTo="body"
            >
                <ng-template #selectedItem>
                    <div class="d-flex flex-wrap gap-1">
                        @for (lang of ocrLanguagesSelected; track lang) {
                        <p-chip
                            [label]="getOcrLanguageLabel(lang)"
                            [removable]="true"
                            (onRemove)="removeOcrLanguage($event, lang)"
                        />
                        }
                    </div>
                </ng-template>
            </p-select>
        </div>
    </div>

    <ng-template #footer>
        <p-button
            label="Abbrechen"
            i18n-label="@@cancel"
            severity="secondary"
            [outlined]="true"
            (click)="close()"
        />
        <p-button
            label="Jetzt extrahieren"
            i18n-label="@@extraction-start"
            [loading]="extractionLoading"
            (click)="triggerExtraction()"
        />
    </ng-template>
</p-dialog>